import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useTranslation } from 'react-i18next'
import { useOnboardingStore } from '@/store/onboardingStore'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { 
  CheckCircle,
  Rocket,
  BookOpen,
  BarChart3,
  Settings,
  FileText,
  ArrowRight,
  Sparkles,
  Trophy
} from 'lucide-react'

export function CompletionStep() {
  const { t, i18n } = useTranslation()
  const { setComplete } = useOnboardingStore()
  const { user } = useAuth()
  const navigate = useNavigate()

  const handleComplete = async () => {
    try {
      // 1. Mark onboarding as completed in the database
      if (user?.id) {
        const { error } = await supabase
          .from('profiles')
          .update({ 
            onboarding_completed: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id)
          
        if (error) {
          console.error('Error marking onboarding as completed:', error)
          toast.error('Failed to save onboarding status')
        }
      }
      
      // 2. Update local state
      setComplete(true)
      
      // 3. Save language preference to localStorage
      localStorage.setItem('i18nextLng', i18n.language)
      
      // 4. Navigate to dashboard
      navigate('/app/dashboard')
      
      // 5. Show success message
      toast.success('Welcome to Flows.Cash! Your financial journey begins now.')
    } catch (error) {
      console.error('Error completing onboarding:', error)
      toast.error('Failed to complete onboarding')
    }
  }

  const nextSteps = [
    {
      icon: <FileText className="h-5 w-5 text-[#439476]" />,
      title: t('onboarding.completion.steps.transactions', 'Add More Transactions'),
      description: t('onboarding.completion.steps.transactionsDesc', 'Import your bank statements or add transactions manually'),
      path: '/app/transactions'
    },
    {
      icon: <BarChart3 className="h-5 w-5 text-[#439476]" />,
      title: t('onboarding.completion.steps.analytics', 'Explore Analytics'),
      description: t('onboarding.completion.steps.analyticsDesc', 'Discover insights about your spending patterns'),
      path: '/app/analytics'
    },
    {
      icon: <Settings className="h-5 w-5 text-[#439476]" />,
      title: t('onboarding.completion.steps.customize', 'Customize Settings'),
      description: t('onboarding.completion.steps.customizeDesc', 'Fine-tune categories, preferences, and notifications'),
      path: '/app/settings'
    }
  ]

  const achievements = [
    {
      icon: <CheckCircle className="h-4 w-4 text-green-500" />,
      text: t('onboarding.completion.achievements.profile', 'Profile set up')
    },
    {
      icon: <CheckCircle className="h-4 w-4 text-green-500" />,
      text: t('onboarding.completion.achievements.account', 'First account added')
    },
    {
      icon: <CheckCircle className="h-4 w-4 text-green-500" />,
      text: t('onboarding.completion.achievements.categories', 'Categories organized')
    },
    {
      icon: <CheckCircle className="h-4 w-4 text-green-500" />,
      text: t('onboarding.completion.achievements.transaction', 'First transaction recorded')
    }
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-6">
        <div className="relative mx-auto w-20 h-20 mb-6">
          <div className="absolute inset-0 bg-gradient-to-r from-[#439476] to-[#387565] rounded-full opacity-20 animate-pulse"></div>
          <div className="relative bg-gradient-to-r from-[#439476] to-[#387565] rounded-full p-4 shadow-lg">
            <Trophy className="h-12 w-12 text-white" />
          </div>
          <div className="absolute -top-2 -right-2">
            <Sparkles className="h-8 w-8 text-yellow-400 animate-bounce" />
          </div>
        </div>
        
        <div className="space-y-3">
          <h3 className="text-3xl font-bold bg-gradient-to-r from-[#1C1C1C] to-[#383838] dark:from-[#E8E8E8] dark:to-[#CCCCCC] bg-clip-text text-transparent">
            {t('onboarding.completion.title', 'Congratulations! 🎉')}
          </h3>
          <p className="text-lg text-[#439476] font-medium">
            {t('onboarding.completion.subtitle', 'You\'re all set up and ready to take control of your finances!')}
          </p>
          <p className="text-muted-foreground">
            {t('onboarding.completion.description', 'Your financial command center is ready. Let\'s explore what you can do next.')}
          </p>
        </div>
      </div>

      {/* Achievements */}
      <Card className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-800">
        <div className="space-y-4">
          <h4 className="font-semibold text-green-900 dark:text-green-100 flex items-center gap-2">
            <Trophy className="h-5 w-5 text-green-600 dark:text-green-400" />
            {t('onboarding.completion.achievementsTitle', 'Setup Complete!')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {achievements.map((achievement, index) => (
              <div key={index} className="flex items-center gap-2">
                {achievement.icon}
                <span className="text-sm text-green-800 dark:text-green-200">{achievement.text}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Next Steps */}
      <div className="space-y-4">
        <h4 className="font-semibold text-base flex items-center gap-2">
          <Rocket className="h-5 w-5 text-[#439476]" />
          {t('onboarding.completion.nextStepsTitle', 'What\'s Next?')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {nextSteps.map((step, index) => (
            <Card key={index} className="p-4 hover:shadow-md transition-all duration-200 cursor-pointer bg-white/80 dark:bg-[#182438]/80">
              <div className="space-y-3">
                <div className="p-2 rounded-lg bg-[#439476]/10 w-fit">
                  {step.icon}
                </div>
                <div className="space-y-1">
                  <h5 className="font-medium text-sm">{step.title}</h5>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Tips Card */}
      <Card className="p-6 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <BookOpen className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <h4 className="font-medium text-blue-900 dark:text-blue-100 text-sm">
              {t('onboarding.completion.tipsTitle', 'Pro Tips for Success')}
            </h4>
            <ul className="space-y-1 text-blue-700 dark:text-blue-300 text-sm">
              <li>• {t('onboarding.completion.tips.consistent', 'Record transactions regularly for better insights')}</li>
              <li>• {t('onboarding.completion.tips.categories', 'Review and adjust categories as your spending evolves')}</li>
              <li>• {t('onboarding.completion.tips.budgets', 'Set realistic budgets that motivate rather than restrict')}</li>
              <li>• {t('onboarding.completion.tips.goals', 'Break large goals into smaller, achievable milestones')}</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Call to Action */}
      <div className="text-center space-y-6">
        <p className="text-muted-foreground">
          {t('onboarding.completion.readyMessage', 'Ready to start your financial journey?')}
        </p>
        
        <Button 
          onClick={handleComplete}
          className="bg-gradient-to-r from-[#439476] to-[#387565] hover:from-[#387565] hover:to-[#2d5c4a] text-white px-8 py-3 text-lg font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 mx-auto"
        >
          {t('onboarding.completion.enterApp', 'Enter Flows.Cash')}
          <ArrowRight className="w-5 h-5" />
        </Button>
        
        <p className="text-xs text-muted-foreground">
          {t('onboarding.completion.helpText', 'Need help? Check out our help center or contact support anytime.')}
        </p>
      </div>
    </div>
  )
} 